import { AnalyticsService } from '@alison/analytics/client';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useRef, useState } from 'react';
import { formatDuration } from '../../lib/dateUtils';
import { CreativeIdLine } from './CreativeIdLine';
import { CreativeScoreBadge } from './CreativeScoreBadge';
import { CreativeScorePopover } from './CreativeScorePopover';
import type { CreativeRow, CreativesListData, MetricCatalog } from './data/api';
import { CREATIVE_SCORE_METRIC_NAME, type DisplayedMetrics } from './data/displayedMetrics';
import { formatDelta, formatMetricValue } from './data/format';
import { MetricsDensityToolbar } from './MetricsDensityToolbar';
import { NetworkBadge } from './NetworkBadge';
import {
  deltaColor,
  type MetricsMode,
  NO_CREATIVES_MESSAGE,
  scaleWithDensity,
  TOOLBAR_BUTTON_SX,
  usePagedList,
} from './utils';

const PAGE_SIZE = 50;

const MAX_VISIBLE_METRICS = 6;

const CARD_SX = {
  borderRadius: 1,
  overflow: 'hidden',
  bgcolor: 'background.paper',
  transition: 'border-color 120ms ease, transform 120ms ease, box-shadow 120ms ease',
};

const METRICS_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  rowGap: 1,
  columnGap: 3,
};

interface CreativesGridProps {
  data: CreativesListData | undefined;
  catalog: MetricCatalog | undefined;
  applied: DisplayedMetrics;
  pending: boolean;
  onOpen: (creativeId: string) => void;
  leadingKpiLabel: string;
}

function metricsPanelHeight(columnCount: number): number {
  const rows = Math.max(1, Math.ceil(columnCount / 2));
  return rows * 28 + (rows - 1) * 5 + 20;
}

function gridSx(density: number) {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${scaleWithDensity(density, 220, 380)}px, 1fr))`,
    gap: 4,
    transition: 'grid-template-columns 150ms ease',
  };
}

function ThumbnailPreview({
  creative,
  columns,
  catalog,
  sortColumn,
  currencySymbol,
  thumbnailHeight,
  showMetrics,
  hovered,
  leadingKpiLabel,
}: {
  creative: CreativeRow;
  columns: CreativesListData['columns'];
  catalog: MetricCatalog | undefined;
  sortColumn: CreativesListData['columns'][number] | undefined;
  currencySymbol: string;
  thumbnailHeight: number;
  showMetrics: boolean;
  hovered: boolean;
  leadingKpiLabel: string;
}): ReactNode {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const isImage = creative.creativeType?.toLowerCase() === 'image';
  const isVideo = creative.creativeType?.toLowerCase() === 'video';
  const pillText = [hovered ? creative.format : null, formatDuration(creative.duration)]
    .filter((part) => part !== null && part !== '')
    .join(' · ');
  const hasVideoPreview = Boolean(creative.previewUrl) && !isImage;
  const expandableColumns = columns.filter(
    (column) => column.name !== sortColumn?.name && column.name !== CREATIVE_SCORE_METRIC_NAME,
  );
  const visibleColumns = expandableColumns.slice(0, MAX_VISIBLE_METRICS);
  const overflowColumns = expandableColumns.slice(MAX_VISIBLE_METRICS);
  const sortValue = sortColumn === undefined ? null : (creative.metrics[sortColumn.name] ?? null);

  function handleMouseEnter(): void {
    if (!hasVideoPreview) return;
    setIsPlaying(true);
    videoRef.current?.play().catch(() => setIsPlaying(false));
  }

  function handleMouseLeave(): void {
    setIsPlaying(false);
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: thumbnailHeight,
        overflow: 'hidden',
        bgcolor: 'background.page',
        transition: 'height 150ms ease',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {thumbnailFailed ? null : (
        <Box
          component="img"
          src={creative.thumbnailUrl}
          alt=""
          onError={() => setThumbnailFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      {hasVideoPreview && (
        <Box
          component="video"
          ref={videoRef}
          src={creative.previewUrl}
          muted
          loop
          playsInline
          preload="none"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: isPlaying ? 1 : 0,
            transition: 'opacity 150ms ease',
            pointerEvents: 'none',
          }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          alignItems: 'flex-start',
        }}
      >
        <NetworkBadge network={creative.network} />
        {!isVideo && pillText === '' ? null : (
          <Box
            data-test="creative-type-pill"
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 0.6,
              bgcolor: alpha(theme.palette.common.black, 0.6),
              color: 'common.white',
            })}
          >
            {isVideo && <PlayArrowRounded titleAccess="Video" sx={{ fontSize: 16 }} />}
            {pillText === '' ? null : <Typography variant="overline">{pillText}</Typography>}
          </Box>
        )}
      </Box>
      {creative.creativeScore !== undefined && creative.creativeScore.score !== null && (
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <CreativeScorePopover
            creativeScore={creative.creativeScore}
            leadingKpiLabel={leadingKpiLabel}
          >
            <Box
              sx={(theme) => ({
                bgcolor: alpha(theme.palette.common.white, 0.75),
                borderRadius: 1,
                p: 1,
                display: 'flex',
              })}
            >
              <CreativeScoreBadge creativeScore={creative.creativeScore} />
            </Box>
          </CreativeScorePopover>
        </Box>
      )}
      {hasVideoPreview && !isPlaying && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: 'background.paper',
            }}
          >
            <PlayArrowRounded sx={{ fontSize: 22, color: 'text.primary' }} />
          </Box>
        </Box>
      )}
      <Box
        sx={(theme) => ({
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          background: `linear-gradient(to top, ${alpha(theme.palette.common.black, 1)} 0%, ${alpha(theme.palette.common.black, 0.85)} 60%, ${alpha(theme.palette.common.black, 0)} 100%)`,
          pt: 12,
        })}
      >
        <Box
          sx={{
            overflow: 'hidden',
            maxHeight: showMetrics
              ? metricsPanelHeight(visibleColumns.length + (overflowColumns.length > 0 ? 2 : 0))
              : 0,
            transition: 'max-height 200ms ease',
          }}
        >
          <Box sx={{ ...METRICS_GRID_SX, px: 3 }}>
            {visibleColumns.map((column) => (
              <MetricCell
                key={column.name}
                column={column}
                creative={creative}
                catalog={catalog}
                currencySymbol={currencySymbol}
              />
            ))}
            {overflowColumns.length > 0 && (
              <MetricsOverflowButton
                columns={overflowColumns}
                creative={creative}
                catalog={catalog}
                currencySymbol={currencySymbol}
              />
            )}
          </Box>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          {hovered && (
            <Box sx={{ mb: 0.5 }}>
              <CreativeIdLine creativeId={creative.creativeId} onDark />
            </Box>
          )}
          {sortColumn === undefined ? null : (
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  color: 'common.white',
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatMetricValue(sortValue, sortColumn.format, currencySymbol)}
              </Typography>
              <Typography
                variant="caption"
                sx={(theme) => ({ color: alpha(theme.palette.common.white, 0.7) })}
              >
                {sortColumn.label}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function MetricsOverflowButton({
  columns,
  creative,
  catalog,
  currencySymbol,
}: {
  columns: CreativesListData['columns'];
  creative: CreativeRow;
  catalog: MetricCatalog | undefined;
  currencySymbol: string;
}): ReactNode {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <Typography
        component="button"
        type="button"
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={(theme) => ({
          border: 'none',
          background: 'none',
          p: 0,
          cursor: 'pointer',
          color: alpha(theme.palette.common.white, 0.6),
          transition: theme.transitions.create('color', {
            duration: theme.transitions.duration.shortest,
          }),
          '&:hover': { color: 'common.white' },
        })}
      >
        {`+${columns.length} more`}
      </Typography>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { bgcolor: 'common.black', p: 2, borderRadius: 1, minWidth: 220 } },
        }}
      >
        <Box sx={METRICS_GRID_SX}>
          {columns.map((column) => (
            <MetricCell
              key={column.name}
              column={column}
              creative={creative}
              catalog={catalog}
              currencySymbol={currencySymbol}
            />
          ))}
        </Box>
      </Popover>
    </Box>
  );
}

function MetricCell({
  column,
  creative,
  catalog,
  currencySymbol,
}: {
  column: CreativesListData['columns'][number];
  creative: CreativeRow;
  catalog: MetricCatalog | undefined;
  currencySymbol: string;
}): ReactNode {
  const value = creative.metrics[column.name] ?? null;
  const improvement = creative.uplift[column.name] ?? null;
  const lowerIsBetter = catalog?.[column.name]?.lowerIsBetter === true;
  const change = improvement === null ? null : lowerIsBetter ? -improvement : improvement;

  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, width: '100%' }}>
      <Typography
        variant="body2"
        sx={(theme) => ({
          minWidth: 44,
          flexShrink: 0,
          color: alpha(theme.palette.common.white, 0.6),
        })}
      >
        {column.label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          minWidth: 0,
        }}
      >
        <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums', color: 'common.white' }}>
          {formatMetricValue(value, column.format, currencySymbol)}
        </Typography>
        {change === null ? null : (
          <Typography
            variant="caption"
            noWrap
            sx={(theme) => ({
              fontVariantNumeric: 'tabular-nums',
              color: deltaColor(improvement, alpha(theme.palette.common.white, 0.6)),
            })}
          >
            {formatDelta(change)}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function CreativeCard({
  creative,
  columns,
  catalog,
  currencySymbol,
  metricsMode,
  thumbnailHeight,
  sortMetric,
  onOpen,
  leadingKpiLabel,
}: {
  creative: CreativeRow;
  columns: CreativesListData['columns'];
  catalog: MetricCatalog | undefined;
  currencySymbol: string;
  metricsMode: MetricsMode;
  thumbnailHeight: number;
  sortMetric: string;
  onOpen: (creativeId: string) => void;
  leadingKpiLabel: string;
}): ReactNode {
  const [hovered, setHovered] = useState(false);
  const sortColumn = columns.find((column) => column.name === sortMetric);
  const showMetrics = metricsMode === 'fixed' || hovered;

  return (
    <Box
      onClick={() => onOpen(creative.creativeId)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={(theme) => ({
        ...CARD_SX,
        cursor: 'pointer',
        border: '2px solid',
        borderColor:
          hovered && metricsMode === 'hover' ? theme.palette.primary.main : 'transparent',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[6],
        },
      })}
    >
      <ThumbnailPreview
        creative={creative}
        columns={columns}
        catalog={catalog}
        sortColumn={sortColumn}
        currencySymbol={currencySymbol}
        thumbnailHeight={thumbnailHeight}
        showMetrics={showMetrics}
        hovered={hovered}
        leadingKpiLabel={leadingKpiLabel}
      />
    </Box>
  );
}

function SkeletonCard({ thumbnailHeight }: { thumbnailHeight: number }): ReactNode {
  return (
    <Box sx={{ ...CARD_SX, border: '2px solid', borderColor: 'transparent' }}>
      <Skeleton variant="rectangular" sx={{ height: thumbnailHeight }} />
    </Box>
  );
}

export function CreativesGrid({
  data,
  catalog,
  applied,
  pending,
  onOpen,
  leadingKpiLabel,
}: CreativesGridProps): ReactNode {
  const [metricsMode, setMetricsMode] = useState<MetricsMode>('hover');
  const [density, setDensity] = useState(60);

  const columns =
    data?.columns ??
    applied.metrics.map((name) => ({
      name,
      label: catalog?.[name]?.label ?? name,
      format: 'number' as const,
    }));
  const creatives = data?.creatives ?? [];
  const currencySymbol = data?.currencySymbol ?? '';

  const isLoading = pending && data === undefined;
  const thumbnailHeight = scaleWithDensity(density, 140, 300);
  const {
    visibleItems: visibleCreatives,
    hasMore,
    loadMore,
  } = usePagedList(data, creatives, PAGE_SIZE);

  return (
    <Box>
      <MetricsDensityToolbar
        metricsMode={metricsMode}
        onMetricsModeChange={setMetricsMode}
        density={density}
        onDensityChange={setDensity}
      />

      {isLoading ? (
        <Box sx={gridSx(density)}>
          {Array.from({ length: 8 }, (_, index) => `skeleton-${index}`).map((id) => (
            <SkeletonCard key={id} thumbnailHeight={thumbnailHeight} />
          ))}
        </Box>
      ) : data !== undefined && creatives.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <Typography color="text.secondary">{NO_CREATIVES_MESSAGE}</Typography>
        </Box>
      ) : (
        <Box data-test="creatives-cards" sx={gridSx(density)}>
          {visibleCreatives.map((creative) => (
            <CreativeCard
              key={creative.creativeId}
              creative={creative}
              columns={columns}
              catalog={catalog}
              currencySymbol={currencySymbol}
              metricsMode={metricsMode}
              thumbnailHeight={thumbnailHeight}
              sortMetric={applied.sort}
              onOpen={onOpen}
              leadingKpiLabel={leadingKpiLabel}
            />
          ))}
        </Box>
      )}

      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <Button
            variant="outlined"
            data-test="creatives-load-more"
            onClick={() => {
              AnalyticsService.track('Explore - Load More');
              loadMore();
            }}
            sx={TOOLBAR_BUTTON_SX}
          >
            Load more
          </Button>
        </Box>
      )}
    </Box>
  );
}
